import Phaser from 'phaser';
import type {
  GameRuntime,
  GameRuntimeConfig,
  GameRuntimeEvents,
  GameRuntimeState,
} from '@adgf/game-engine';

/**
 * Gem Match — Match-3 puzzle game
 *
 * Swap adjacent gems to match 3 or more in a row or column.
 * Chain combos for bonus points. 60-second time limit.
 */

const GRID_SIZE = 8;
const CELL_SIZE = 64;
const BOARD_OFFSET_X = 40;
const BOARD_OFFSET_Y = 100;
const GAME_WIDTH = BOARD_OFFSET_X * 2 + GRID_SIZE * CELL_SIZE;
const GAME_HEIGHT = BOARD_OFFSET_Y + GRID_SIZE * CELL_SIZE + 60;
const DURATION_MS = 60_000;

// Modern vibrant gem palette with gradient pairs
const GEM_STYLES = [
  { base: 0xff6b6b, light: 0xff8e8e, dark: 0xee5253, glow: 0xff6b6b },  // coral red
  { base: 0xffd93d, light: 0xffe66d, dark: 0xf4c430, glow: 0xffd93d },  // golden yellow
  { base: 0x6bcb77, light: 0x8ce08f, dark: 0x4daf5a, glow: 0x6bcb77 },  // mint green
  { base: 0x4d96ff, light: 0x6db3ff, dark: 0x3a7de0, glow: 0x4d96ff },  // ocean blue
  { base: 0xa855f7, light: 0xc084fc, dark: 0x9333ea, glow: 0xa855f7 },  // vivid purple
  { base: 0xff9f43, light: 0xffb870, dark: 0xf08c2e, glow: 0xff9f43 },  // orange
];

const COLOR_BG = 0x0f0f1e;
const COLOR_ACCENT = 0xff6b6b;
const COLOR_ACCENT_STR = '#ff6b6b';
const COLOR_CARD_BG = 0x1e1e38;
const COLOR_TEXT_SECONDARY = '#a0a0c0';

interface Gem {
  row: number;
  col: number;
  color: number;
  sprite: Phaser.GameObjects.Container;
}

interface SceneCallbacks {
  onSwap(fromRow: number, fromCol: number, toRow: number, toCol: number): void;
  onMatch(count: number, combo: number): void;
  onGameOver(score: number): void;
  wantsStart(): boolean;
}

class Match3Scene extends Phaser.Scene {
  private readonly cb: SceneCallbacks;
  private gems: (Gem | null)[][] = [];
  private selected: Gem | null = null;
  private isBusy = false;

  private scoreText: Phaser.GameObjects.Text | null = null;
  private timerText: Phaser.GameObjects.Text | null = null;
  private comboText: Phaser.GameObjects.Text | null = null;
  private readyOverlay: Phaser.GameObjects.Container | null = null;
  private overOverlay: Phaser.GameObjects.Container | null = null;

  private remainingMs = DURATION_MS;
  private running = false;
  private created = false;
  private combo = 0;

  constructor(cb: SceneCallbacks) {
    super({ key: 'Match3Scene' });
    this.cb = cb;
  }

  create(): void {
    const { width } = this.scale;

    // Background gradient effect
    const bg = this.add.rectangle(width / 2, this.scale.height / 2, width, this.scale.height, COLOR_BG);
    bg.setScrollFactor(0);

    // Score card background
    const scoreCard = this.add.rectangle(BOARD_OFFSET_X + 80, 30, 160, 50, COLOR_CARD_BG, 0.9);
    scoreCard.setStrokeStyle(2, COLOR_ACCENT, 0.3);
    scoreCard.setOrigin(0.5);

    // Score text
    this.scoreText = this.add
      .text(BOARD_OFFSET_X, 30, 'Score: 0', {
        fontSize: '22px',
        color: COLOR_ACCENT_STR,
        fontStyle: 'bold',
        fontFamily: 'Arial, sans-serif',
      })
      .setOrigin(0, 0.5);

    // Timer card background
    const timerCard = this.add.rectangle(width - BOARD_OFFSET_X - 45, 30, 90, 50, COLOR_CARD_BG, 0.9);
    timerCard.setStrokeStyle(2, 0x4d96ff, 0.3);
    timerCard.setOrigin(0.5);

    // Timer text
    this.timerText = this.add
      .text(width - BOARD_OFFSET_X, 30, '60', {
        fontSize: '32px',
        color: '#4d96ff',
        fontStyle: 'bold',
        fontFamily: 'Arial, sans-serif',
      })
      .setOrigin(1, 0.5);

    // Combo text (center)
    this.comboText = this.add
      .text(width / 2, 30, '', {
        fontSize: '20px',
        color: '#ffd93d',
        fontStyle: 'bold',
        fontFamily: 'Arial, sans-serif',
      })
      .setOrigin(0.5);

    // Build board
    this.buildBoard();

    // Overlays
    this.buildReadyOverlay();
    this.buildGameOverOverlay();

    this.created = true;

    if (this.cb.wantsStart()) {
      this.beginRound();
    }
  }

  requestBegin(): void {
    if (this.created) this.beginRound();
  }

  beginRound(): void {
    this.running = true;
    this.remainingMs = DURATION_MS;
    this.combo = 0;
    this.hideOverlay(this.readyOverlay);
    this.hideOverlay(this.overOverlay);
  }

  setScoreDisplay(score: number): void {
    this.scoreText?.setText(`Score: ${score}`);
  }

  setComboDisplay(combo: number): void {
    if (combo > 1) {
      this.comboText?.setText(`Combo x${combo}!`);
    } else {
      this.comboText?.setText('');
    }
  }

  override update(_time: number, delta: number): void {
    if (!this.running) return;
    this.remainingMs -= delta;
    const secondsLeft = Math.max(0, Math.ceil(this.remainingMs / 1000));
    this.timerText?.setText(String(secondsLeft));
    if (this.remainingMs <= 0) {
      this.endRound();
    }
  }

  // ---- Board logic ----

  private buildBoard(): void {
    this.gems = [];
    for (let row = 0; row < GRID_SIZE; row++) {
      this.gems[row] = [];
      for (let col = 0; col < GRID_SIZE; col++) {
        let styleIndex: number;
        do {
          styleIndex = Phaser.Math.Between(0, GEM_STYLES.length - 1);
        } while (this.wouldCauseImmediateMatch(row, col, styleIndex));
        this.gems[row][col] = this.createGem(row, col, styleIndex);
      }
    }
  }

  private wouldCauseImmediateMatch(row: number, col: number, styleIndex: number): boolean {
    // Check horizontal
    if (col >= 2 && this.gems[row][col - 1]?.color === styleIndex && this.gems[row][col - 2]?.color === styleIndex) {
      return true;
    }
    // Check vertical
    if (row >= 2 && this.gems[row - 1][col]?.color === styleIndex && this.gems[row - 2][col]?.color === styleIndex) {
      return true;
    }
    return false;
  }

  private createGem(row: number, col: number, styleIndex: number): Gem {
    const x = BOARD_OFFSET_X + col * CELL_SIZE + CELL_SIZE / 2;
    const y = BOARD_OFFSET_Y + row * CELL_SIZE + CELL_SIZE / 2;
    const style = GEM_STYLES[styleIndex];

    const container = this.add.container(x, y);

    // Shadow under gem
    const shadow = this.add.ellipse(2, 4, CELL_SIZE * 0.85, CELL_SIZE * 0.85, 0x000000, 0.3);
    container.add(shadow);

    // Main gem body with gradient effect
    const gem = this.add.circle(0, 0, CELL_SIZE / 2 - 8, style.base);
    container.add(gem);

    // Inner highlight (top-left)
    const highlight = this.add.circle(-6, -6, CELL_SIZE / 6, style.light, 0.6);
    container.add(highlight);

    // Small sparkle
    const sparkle = this.add.circle(-10, -10, 3, 0xffffff, 0.9);
    container.add(sparkle);

    // Set interactive on the container itself (Phaser best practice)
    container.setSize(CELL_SIZE, CELL_SIZE);
    container.setInteractive(
      new Phaser.Geom.Circle(0, 0, CELL_SIZE / 2),
      Phaser.Geom.Circle.Contains,
      true
    );
    container.on('pointerdown', () => this.onGemClick(row, col));

    return { row, col, color: styleIndex, sprite: container };
  }

  private onGemClick(row: number, col: number): void {
    if (!this.running || this.isBusy) return;

    const gem = this.gems[row][col];
    if (!gem) return;

    if (!this.selected) {
      // First selection
      this.selected = gem;
      gem.sprite.setScale(1.1);
      gem.sprite.setAlpha(0.9);
    } else if (this.selected === gem) {
      // Deselect
      this.selected.sprite.setScale(1);
      this.selected.sprite.setAlpha(1);
      this.selected = null;
    } else if (this.isAdjacent(this.selected, gem)) {
      // Swap
      const from = this.selected;
      this.selected.sprite.setScale(1);
      this.selected.sprite.setAlpha(1);
      this.selected = null;
      this.trySwap(from, gem);
    } else {
      // New selection
      this.selected.sprite.setScale(1);
      this.selected.sprite.setAlpha(1);
      this.selected = gem;
      gem.sprite.setScale(1.1);
      gem.sprite.setAlpha(0.9);
    }
  }

  private isAdjacent(a: Gem, b: Gem): boolean {
    const dr = Math.abs(a.row - b.row);
    const dc = Math.abs(a.col - b.col);
    return (dr === 1 && dc === 0) || (dr === 0 && dc === 1);
  }

  private async trySwap(a: Gem, b: Gem): Promise<void> {
    this.isBusy = true;
    this.cb.onSwap(a.row, a.col, b.row, b.col);

    // Animate swap
    await this.animateSwap(a, b);

    // Swap in grid
    this.swapInGrid(a, b);

    // Check for matches
    const matches = this.findMatches();
    if (matches.length === 0) {
      // No match, swap back
      await this.animateSwap(a, b);
      this.swapInGrid(a, b);
    } else {
      // Process matches
      this.combo = 0;
      await this.processMatches(matches);
    }

    this.isBusy = false;
  }

  private swapInGrid(a: Gem, b: Gem): void {
    this.gems[a.row][a.col] = b;
    this.gems[b.row][b.col] = a;
    const aRow = a.row;
    const aCol = a.col;
    a.row = b.row;
    a.col = b.col;
    b.row = aRow;
    b.col = aCol;
  }

  private animateSwap(a: Gem, b: Gem): Promise<void> {
    return new Promise((resolve) => {
      const aPos = { x: a.sprite.x, y: a.sprite.y };
      const bPos = { x: b.sprite.x, y: b.sprite.y };

      this.tweens.add({
        targets: a.sprite,
        x: bPos.x,
        y: bPos.y,
        duration: 150,
        ease: 'Power2',
      });
      this.tweens.add({
        targets: b.sprite,
        x: aPos.x,
        y: aPos.y,
        duration: 150,
        ease: 'Power2',
        onComplete: () => resolve(),
      });
    });
  }

  private findMatches(): Gem[] {
    const matched = new Set<Gem>();

    // Horizontal matches
    for (let row = 0; row < GRID_SIZE; row++) {
      let runStart = 0;
      for (let col = 1; col <= GRID_SIZE; col++) {
        const prev = this.gems[row][col - 1];
        const curr = col < GRID_SIZE ? this.gems[row][col] : null;
        if (!curr || !prev || curr.color !== prev.color) {
          if (col - runStart >= 3) {
            for (let c = runStart; c < col; c++) {
              const gem = this.gems[row][c];
              if (gem) matched.add(gem);
            }
          }
          runStart = col;
        }
      }
    }

    // Vertical matches
    for (let col = 0; col < GRID_SIZE; col++) {
      let runStart = 0;
      for (let row = 1; row <= GRID_SIZE; row++) {
        const prev = this.gems[row - 1][col];
        const curr = row < GRID_SIZE ? this.gems[row][col] : null;
        if (!curr || !prev || curr.color !== prev.color) {
          if (row - runStart >= 3) {
            for (let r = runStart; r < row; r++) {
              const gem = this.gems[r][col];
              if (gem) matched.add(gem);
            }
          }
          runStart = row;
        }
      }
    }

    return [...matched];
  }

  private async processMatches(matches: Gem[]): Promise<void> {
    this.combo++;
    this.cb.onMatch(matches.length, this.combo);
    this.setComboDisplay(this.combo);

    // Animate removal
    await Promise.all(
      matches.map(
        (gem) =>
          new Promise<void>((resolve) => {
            this.tweens.add({
              targets: gem.sprite,
              scale: 0,
              alpha: 0,
              duration: 200,
              onComplete: () => {
                gem.sprite.destroy();
                resolve();
              },
            });
          }),
      ),
    );

    // Remove from grid
    for (const gem of matches) {
      this.gems[gem.row][gem.col] = null;
    }

    // Drop gems down
    await this.dropGems();

    // Refill top
    await this.refillGems();

    // Check for new matches (chain reaction)
    const newMatches = this.findMatches();
    if (newMatches.length > 0) {
      await this.processMatches(newMatches);
    } else {
      // Reset combo after chain ends
      this.time.delayedCall(500, () => {
        this.combo = 0;
        this.setComboDisplay(0);
      });
    }
  }

  private async dropGems(): Promise<void> {
    const animations: Promise<void>[] = [];

    for (let col = 0; col < GRID_SIZE; col++) {
      let writeRow = GRID_SIZE - 1;
      for (let row = GRID_SIZE - 1; row >= 0; row--) {
        const gem = this.gems[row][col];
        if (gem) {
          if (writeRow !== row) {
            this.gems[writeRow][col] = gem;
            this.gems[row][col] = null;
            gem.row = writeRow;
            const targetY = BOARD_OFFSET_Y + writeRow * CELL_SIZE + CELL_SIZE / 2;
            animations.push(
              new Promise<void>((resolve) => {
                this.tweens.add({
                  targets: gem.sprite,
                  y: targetY,
                  duration: 200,
                  ease: 'Bounce.easeOut',
                  onComplete: () => resolve(),
                });
              }),
            );
          }
          writeRow--;
        }
      }
    }

    await Promise.all(animations);
  }

  private async refillGems(): Promise<void> {
    const animations: Promise<void>[] = [];

    for (let col = 0; col < GRID_SIZE; col++) {
      for (let row = 0; row < GRID_SIZE; row++) {
        if (!this.gems[row][col]) {
          const styleIndex = Phaser.Math.Between(0, GEM_STYLES.length - 1);
          const style = GEM_STYLES[styleIndex];
          const x = BOARD_OFFSET_X + col * CELL_SIZE + CELL_SIZE / 2;
          const y = BOARD_OFFSET_Y - CELL_SIZE + row * CELL_SIZE;

          const container = this.add.container(x, y - GRID_SIZE * CELL_SIZE);

          // Shadow
          const shadow = this.add.ellipse(2, 4, CELL_SIZE * 0.85, CELL_SIZE * 0.85, 0x000000, 0.3);
          container.add(shadow);

          // Main gem
          const gem = this.add.circle(0, 0, CELL_SIZE / 2 - 8, style.base);
          container.add(gem);

          // Highlight
          const highlight = this.add.circle(-6, -6, CELL_SIZE / 6, style.light, 0.6);
          container.add(highlight);

          // Sparkle
          const sparkle = this.add.circle(-10, -10, 3, 0xffffff, 0.9);
          container.add(sparkle);

          // Set interactive
          container.setSize(CELL_SIZE, CELL_SIZE);
          container.setInteractive(
            new Phaser.Geom.Circle(0, 0, CELL_SIZE / 2),
            Phaser.Geom.Circle.Contains,
            true
          );
          container.on('pointerdown', () => this.onGemClick(row, col));

          const gemObj: Gem = { row, col, color: styleIndex, sprite: container };
          this.gems[row][col] = gemObj;

          const targetY = BOARD_OFFSET_Y + row * CELL_SIZE + CELL_SIZE / 2;
          animations.push(
            new Promise<void>((resolve) => {
              this.tweens.add({
                targets: container,
                y: targetY,
                duration: 250,
                ease: 'Bounce.easeOut',
                onComplete: () => resolve(),
              });
            }),
          );
        }
      }
    }

    await Promise.all(animations);
  }

  private endRound(): void {
    this.running = false;
    this.showOverlay(this.overOverlay);
    this.cb.onGameOver(0); // Score handled by wrapper
  }

  private buildReadyOverlay(): void {
    const { width, height } = this.scale;
    this.readyOverlay = this.add.container(width / 2, height / 2);

    // Overlay background
    const overlayBg = this.add.rectangle(0, 0, width, height, 0x000000, 0.7);
    overlayBg.setOrigin(0.5);
    this.readyOverlay.add(overlayBg);

    // Title with glow effect
    const title = this.add.text(0, -80, 'Gem Match', {
      fontSize: '48px',
      color: '#ffffff',
      fontStyle: 'bold',
      fontFamily: 'Arial, sans-serif',
    });
    title.setOrigin(0.5);

    const subtitle = this.add.text(0, -40, 'Match 3 Gems. Chain Combos. Beat the Clock!', {
      fontSize: '18px',
      color: COLOR_TEXT_SECONDARY,
      fontFamily: 'Arial, sans-serif',
    });
    subtitle.setOrigin(0.5);

    // Modern button with gradient effect
    const btn = this.add.rectangle(0, 40, 240, 70, COLOR_ACCENT);
    btn.setStrokeStyle(3, 0xffffff, 0.2);
    const btnText = this.add.text(0, 40, 'START GAME', {
      fontSize: '26px',
      color: '#ffffff',
      fontStyle: 'bold',
      fontFamily: 'Arial, sans-serif',
    });
    btnText.setOrigin(0.5);

    // Button shadow
    const btnShadow = this.add.rectangle(0, 48, 240, 70, 0x000000, 0.3);
    btnShadow.setOrigin(0.5);

    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerover', () => btn.setFillStyle(16751886));
    btn.on('pointerout', () => btn.setFillStyle(COLOR_ACCENT));
    btn.on('pointerdown', () => {
      if (!this.cb.wantsStart()) this.beginRound();
    });

    this.readyOverlay.add([overlayBg, title, subtitle, btnShadow, btn, btnText]);
  }

  private buildGameOverOverlay(): void {
    const { width, height } = this.scale;
    this.overOverlay = this.add.container(width / 2, height / 2);

    // Overlay background
    const overlayBg = this.add.rectangle(0, 0, width, height, 0x000000, 0.8);
    overlayBg.setOrigin(0.5);
    this.overOverlay.add(overlayBg);

    const title = this.add.text(0, -80, "Time's Up!", {
      fontSize: '48px',
      color: '#ffffff',
      fontStyle: 'bold',
      fontFamily: 'Arial, sans-serif',
    });
    title.setOrigin(0.5);

    const scoreLabel = this.add.text(0, -30, 'Final Score', {
      fontSize: '18px',
      color: COLOR_TEXT_SECONDARY,
      fontFamily: 'Arial, sans-serif',
    });
    scoreLabel.setOrigin(0.5);

    const score = this.add.text(0, 0, '0', {
      fontSize: '42px',
      color: COLOR_ACCENT_STR,
      fontStyle: 'bold',
      fontFamily: 'Arial, sans-serif',
    });
    score.setOrigin(0.5);
    score.setName('finalScoreText');

    // Modern button
    const btn = this.add.rectangle(0, 70, 240, 70, COLOR_ACCENT);
    btn.setStrokeStyle(3, 0xffffff, 0.2);
    const btnText = this.add.text(0, 70, 'PLAY AGAIN', {
      fontSize: '26px',
      color: '#ffffff',
      fontStyle: 'bold',
      fontFamily: 'Arial, sans-serif',
    });
    btnText.setOrigin(0.5);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerover', () => btn.setFillStyle(16751886));
    btn.on('pointerout', () => btn.setFillStyle(COLOR_ACCENT));
    btn.on('pointerdown', () => this.scene.restart());

    this.overOverlay.add([overlayBg, title, scoreLabel, score, btn, btnText]);
    this.overOverlay.setVisible(false);
  }

  private showOverlay(overlay: Phaser.GameObjects.Container | null): void {
    overlay?.setVisible(true);
    overlay?.setActive(true);
  }

  private hideOverlay(overlay: Phaser.GameObjects.Container | null): void {
    overlay?.setVisible(false);
    overlay?.setActive(false);
  }

  showFinalScore(score: number): void {
    const label = this.overOverlay?.getByName('finalScoreText') as Phaser.GameObjects.Text | null;
    label?.setText(String(score));
  }
}

/**
 * Match3Game — GameRuntime wrapper
 */
export class Match3Game implements GameRuntime {
  private readonly config: GameRuntimeConfig;
  private readonly events: GameRuntimeEvents;
  private state: GameRuntimeState = 'idle';
  private score = 0;
  private wantStart = false;
  private game: Phaser.Game | null = null;
  private scene: Match3Scene | null = null;

  constructor(config: GameRuntimeConfig, events: GameRuntimeEvents = {}) {
    this.config = config;
    this.events = events;
  }

  mount(): void {
    if (this.state !== 'idle') return;

    this.scene = new Match3Scene(this.buildCallbacks());

    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: this.config.parent,
      width: GAME_WIDTH,
      height: GAME_HEIGHT,
      backgroundColor: COLOR_BG,
      scale: {
        mode: Phaser.Scale.FIT,
        width: GAME_WIDTH,
        height: GAME_HEIGHT,
      },
      scene: [this.scene],
    });

    this.state = 'mounted';
  }

  start(): void {
    this.wantStart = true;
    if (this.state === 'mounted' || this.state === 'paused' || this.state === 'gameover') {
      this.state = 'running';
      this.events.onStart?.();
      this.scene?.requestBegin();
    }
  }

  pause(): void {
    if (this.state !== 'running') return;
    this.state = 'paused';
    this.scene?.scene?.pause();
    this.events.onPause?.();
  }

  resume(): void {
    if (this.state !== 'paused') return;
    this.state = 'running';
    this.scene?.scene?.resume();
    this.events.onResume?.();
  }

  destroy(): void {
    if (this.state === 'destroyed') return;
    this.game?.destroy(true);
    this.game = null;
    this.scene = null;
    this.state = 'destroyed';
  }

  getScore(): number {
    return this.score;
  }

  getState(): GameRuntimeState {
    return this.state;
  }

  retry(): void {
    this.score = 0;
    this.events.onScoreChange?.(this.score);
    this.state = 'running';
    this.events.onRetry?.();
    this.wantStart = true;
    this.scene?.requestBegin();
  }

  private buildCallbacks(): SceneCallbacks {
    return {
      onSwap: () => {
        this.events.onEvent?.('gem_swap', {});
      },
      onMatch: (count: number, combo: number) => {
        const points = count * 10 * combo;
        this.score += points;
        this.events.onScoreChange?.(this.score);
        this.scene?.setScoreDisplay(this.score);
        this.events.onEvent?.(`match_${Math.min(count, 5)}`, { count, combo, points });
      },
      onGameOver: () => {
        this.state = 'gameover';
        this.scene?.showFinalScore(this.score);
        this.events.onGameOver?.(this.score, { duration: DURATION_MS / 1000 });
      },
      wantsStart: () => this.wantStart,
    };
  }
}

export default Match3Game;
